import type {
  ExperienceSubmission,
  MemoryMapNode,
  Questionnaire,
  RelationStat,
  WordCloudDimension,
  WordStat,
} from '../types/questionnaire'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const PENDING_SUBMISSIONS_KEY = 'taste-memory:pending-submissions'
const QUESTIONNAIRE_CACHE_KEY = 'taste-memory:questionnaire-cache'
const REAL_WORD_STATS_CACHE_KEYS: Record<WordCloudDimension, string> = {
  taste: 'taste-memory:api-word-stats',
  memory_category: 'taste-memory:api-memory-stats',
}
const REAL_MEMORY_MAP_CACHE_KEY = 'taste-memory:api-memory-map-v2'
const REAL_MAP_RELATIONS_CACHE_KEY = 'taste-memory:api-map-relations'

class ApiRequestError extends Error {
  constructor(readonly status: number) {
    super(`Request failed: ${status}`)
    this.name = 'ApiRequestError'
  }
}

export interface SubmissionResult {
  queued: boolean
}

function readStoredJson<T>(key: string): T | null {
  const raw = window.localStorage.getItem(key)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function writeStoredJson(key: string, value: unknown) {
  window.localStorage.setItem(key, JSON.stringify(value))
}

function isRetryableRequestError(error: unknown) {
  if (error instanceof ApiRequestError) {
    return error.status === 408 || error.status === 429 || error.status >= 500
  }

  return error instanceof TypeError
}

function readPendingSubmissions(): ExperienceSubmission[] {
  const stored = readStoredJson<unknown>(PENDING_SUBMISSIONS_KEY)
  if (!Array.isArray(stored)) {
    return []
  }

  return stored.filter((item): item is ExperienceSubmission => {
    if (!item || typeof item !== 'object') {
      return false
    }

    const candidate = item as Partial<ExperienceSubmission>
    return typeof candidate.submissionId === 'string' && Array.isArray(candidate.answers)
  })
}

function enqueueSubmission(payload: ExperienceSubmission) {
  try {
    const pending = readPendingSubmissions()
    if (!pending.some((item) => item.submissionId === payload.submissionId)) {
      pending.push(payload)
      writeStoredJson(PENDING_SUBMISSIONS_KEY, pending)
    }
    return true
  } catch {
    return false
  }
}

export function normalizeCustomTaste(value: string) {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase()
}

export function customTasteStatCode(value: string) {
  const normalized = normalizeCustomTaste(value)
  return normalized ? `custom:${normalized}` : ''
}

export function customMemoryStatCode(value: string) {
  const normalized = normalizeCustomTaste(value)
  return normalized ? `custom:${normalized}` : ''
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  })

  if (!response.ok) {
    throw new ApiRequestError(response.status)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const responseText = await response.text()
  return (responseText ? JSON.parse(responseText) : undefined) as T
}

export async function getActiveQuestionnaire(): Promise<Questionnaire> {
  try {
    const questionnaire = await request<Questionnaire>('/public/questionnaires/active')
    writeStoredJson(QUESTIONNAIRE_CACHE_KEY, questionnaire)
    return questionnaire
  } catch (error) {
    if (!isRetryableRequestError(error)) {
      throw error
    }

    const cachedQuestionnaire = readStoredJson<Questionnaire>(QUESTIONNAIRE_CACHE_KEY)
    if (cachedQuestionnaire) {
      return cachedQuestionnaire
    }
    throw error
  }
}

export async function getWordStats(dimension: WordCloudDimension): Promise<WordStat[]> {
  try {
    const stats = await request<WordStat[]>(`/public/stats/word-cloud?dimension=${dimension}`)
    writeStoredJson(REAL_WORD_STATS_CACHE_KEYS[dimension], stats)
    return stats
  } catch (error) {
    if (!isRetryableRequestError(error)) {
      throw error
    }

    const cachedStats = readStoredJson<WordStat[]>(REAL_WORD_STATS_CACHE_KEYS[dimension])
    if (cachedStats) {
      return cachedStats
    }

    throw error
  }
}

export async function getTasteRelations(tasteCode: string, limit = 5): Promise<RelationStat[]> {
  const cacheKey = `taste-memory:api-relations:${tasteCode}`
  try {
    const relations = await request<RelationStat[]>(
      `/public/stats/relations?tasteCode=${encodeURIComponent(tasteCode)}&limit=${limit}`,
    )
    writeStoredJson(cacheKey, relations)
    return relations
  } catch (error) {
    if (!isRetryableRequestError(error)) {
      throw error
    }

    const cachedRelations = readStoredJson<RelationStat[]>(cacheKey)
    if (cachedRelations) {
      return cachedRelations
    }
    throw error
  }
}

export async function getMapRelations(limit = 100): Promise<RelationStat[]> {
  try {
    const relations = await request<RelationStat[]>(`/public/stats/map-relations?limit=${limit}`)
    writeStoredJson(REAL_MAP_RELATIONS_CACHE_KEY, relations)
    return relations
  } catch (error) {
    if (!isRetryableRequestError(error)) {
      throw error
    }

    const cachedRelations = readStoredJson<RelationStat[]>(REAL_MAP_RELATIONS_CACHE_KEY)
    if (cachedRelations) {
      return cachedRelations
    }
    throw error
  }
}

export async function getMemoryMapNodes(limit = 10): Promise<MemoryMapNode[]> {
  try {
    const entries = await request<MemoryMapNode[]>(`/public/stats/memory-map?limit=${limit}`)
    writeStoredJson(REAL_MEMORY_MAP_CACHE_KEY, entries)
    return entries
  } catch (error) {
    if (!isRetryableRequestError(error)) {
      throw error
    }

    const cachedEntries = readStoredJson<MemoryMapNode[]>(REAL_MEMORY_MAP_CACHE_KEY)
    if (cachedEntries) {
      return cachedEntries
    }
    throw error
  }
}

async function sendSubmission(payload: ExperienceSubmission) {
  await request<void>('/public/submissions', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

let pendingSync: Promise<number> | null = null

export function syncPendingSubmissions(): Promise<number> {
  if (pendingSync) {
    return pendingSync
  }

  pendingSync = (async () => {
    const pending = readPendingSubmissions()
    let syncedCount = 0

    for (let index = 0; index < pending.length; index += 1) {
      try {
        await sendSubmission(pending[index])
        syncedCount += 1
      } catch {
        writeStoredJson(PENDING_SUBMISSIONS_KEY, pending.slice(index))
        return syncedCount
      }
    }

    if (pending.length > 0) {
      window.localStorage.removeItem(PENDING_SUBMISSIONS_KEY)
    }
    return syncedCount
  })().finally(() => {
    pendingSync = null
  })

  return pendingSync
}

export async function submitExperience(payload: ExperienceSubmission): Promise<SubmissionResult> {
  try {
    await sendSubmission(payload)
    return { queued: false }
  } catch (error) {
    if (isRetryableRequestError(error) && enqueueSubmission(payload)) {
      return { queued: true }
    }
    throw error
  }
}
